import React, { useContext, useState, useEffect } from 'react'
import { bool, func } from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { useTheme } from 'emotion-theming'

// Import Components
import { Search as SearchIcon } from '../svgs/Search'
import { SiteConfigContext } from '../SiteConfigProvider'
import { Link } from '@jelly/ui'
import { SearchPanel } from '../Search/SearchPanel'

export const HAMBURGER_MENU_WIDTH = '430px'

// Styled Components via Emotion
const HamburgerMenuWrapper = styled.div`
	padding-top: 10px;
	position: ${({ menuStatus }) => (menuStatus ? 'relative' : 'inherit')};
	width: ${({ menuStatus }) => (menuStatus ? '100%' : 'auto')};
`
const OuterWrapper = styled.div`
	position: absolute;
	z-index: 1000;
`

// HAMBURGER STYLES //
const HamburgerWrapper = styled.div`
	cursor: pointer;
	display: inline-block;
	background: ${({ theme }) => theme.colors.white};
	height: 45px;
	vertical-align: top;
	width: 65px;

	${({ theme }) => theme.MQ.S} {
		height: 55px;
		width: 80px;
	}
`
const Hamburger = styled.div`
	left: 25%;
	top: 25%;
	position: relative;
	width: 35px;
`
const Layer = styled.span`
	background: ${({ theme }) => theme.navigation.linkHoverColor};
	display: block;
	height: 2px;
	margin-bottom: 8px;
	position: relative;

	transform-origin: 4px 0px;

	transition: transform 0.5s cubic-bezier(0.77, 0.2, 0.05, 1), background 0.5s cubic-bezier(0.77, 0.2, 0.05, 1),
		opacity 0.55s ease;

	:nth-of-type(1) {
		transform: ${({ menuStatus }) => (menuStatus ? 'rotate(45deg) translate(2px, -2px)' : 'rotate(0deg)')};
	}
	:nth-of-type(2) {
		display: ${({ menuStatus }) => (menuStatus ? 'none' : 'block')};
		transition: all 1s ease;
	}
	:nth-of-type(3) {
		transform: ${({ menuStatus }) => (menuStatus ? 'rotate(-45deg) translate(-6px, 7px)' : 'rotate(0deg)')};
	}
`

const MenuOverlay = styled.div`
	height: 100vh;
	position: absolute;
	top: 0;
	width: 100%;
	left: ${({ menuStatus }) => (menuStatus ? '0px' : '-100%')};
	transition: all 0.8s ease;
`

// MENU STYLES //
const Menu = styled.div`
	background: ${({ theme }) => theme.colors.white};
	display: inline-block;
	height: 100%;
	padding-top: 80px;
	position: relative;
	width: 100%;
	z-index: 4;

	${({ theme }) => theme.MQ.S} {
		z-index: 5; /* so the account panel always slides in from under the main menu on breakpoints larger than mobile */
		width: ${HAMBURGER_MENU_WIDTH};
	}
`
// DEFAULT TOOL BAR (LINKS FOR :: SEARCH, LOGIN, SIGN UP ) //
const ToolBarDesktop = styled.div`
	display: none;
	margin: 10px 0;
	padding-left: 65px;

	${({ theme }) => theme.MQ.S} {
		${({ theme }) => theme.text.subMenuItem};
		display: flex;
		flex-direction: row;
		padding-left: 85px;
		margin-bottom: 30px;
	}
`

export const StyledLink = styled(Link)`
	color: ${({ theme }) => theme.navigation.linkColor};
	display: flex;
	flex-direction: row;
	align-items: center;
	cursor: pointer;
	line-height: 25px;
	svg {
		padding-left: 10px;
	}
	&:hover {
		color: ${({ theme }) => theme.navigation.linkHoverColor};
		* {
			stroke: ${({ theme }) => theme.navigation.linkHoverColor};
		}
	}
`

// TOOL BAR FOR MOBILE (TOP RIGHT :: SEARCH ICON + LOGIN/ACCOUNT ) //
const ToolBarMobile = styled.div`
	display: inline-block;
	position: absolute;
	right: 0;
	top: 0;

	${({ theme }) => theme.MQ.S} {
		display: none;
	}
`
const ToolIcon = styled.div`
	display: inline-block;
	padding: 15px 10px;
`
const SearchIconWrapper = styled.div`
	height: 19px;
	width: 17px;

	${({ theme }) => theme.MQ.M} {
		height: 17px;
		width: 15px;
	}

	svg:hover {
		* {
			stroke: ${({ theme }) => theme.navigation.linkHoverColor};
		}
	}
`

// LINK ELEMENTS WITHIN MENU; STYLES //
const ElementsWrapper = ({ theme }) => css`
	line-height: 40px;
	margin: 0;
	padding-left: 65px;

	a {
		color: ${theme.navigation.linkColor};
	}

	${theme.MQ.S} {
		padding-left: 85px;
	}
`
const CoreWrapper = styled.ul`
	${ElementsWrapper}
	${({ theme }) => theme.text.menuItem};
	height: auto;
	padding-bottom: 10px;

	${({ theme }) => theme.MQ.XS} {
		padding-bottom: 30px;
	}
`
const BusinessTopicsWrapper = styled.ul`
	${ElementsWrapper}
	${({ theme }) => theme.text.menuItem};
	background: ${({ theme }) => theme.colors.lightGrey};
	height: 100%;
	padding-top: 10px;

	${({ theme }) => theme.MQ.XS} {
		padding-top: 30px;
	}
`
const BusinessTopicsHeadline = styled.li`
	${({ theme }) => theme.text.sectionHeaderSmall};
	margin-bottom: 10px;
	list-style-type: none;
`
const Element = styled.li`
	list-style-type: none;

	a {
		color: ${({ theme }) => theme.navigation.linkColor};

		&:hover {
			color: ${({ theme }) => theme.navigation.linkHoverColor};
		}
	}
`

/* OVERLAY BEHIND THE MENU */
const Overlay = styled.div`
	background: ${({ theme }) => theme.colors.primary};
	height: 100vh;
	position: absolute;
	top: 0;
	opacity: 0.85;
	transition: all 0.2s ease;
	visibility: ${({ showOverlay }) => (showOverlay ? 'visible' : 'hidden')};
	width: 100%;
	/* This is set to 3 because the MBP meatball menu has a z-index of 2 and we want to overlap that when the hamburger menu is open */
	z-index: 3;
`

/**
 * This Component is the Hamburger Menu
 * It should show the Hamburger and business.com logo
 * todo: Make sticky
 *
 * On Open: the menu should slide out from the left side + the hamburger turns into an X
 * A primary overlay should take over the screen, nothing on the screen should be clickable
 * If the user is logged in a second panel also slides out (a little after the regular menu)
 * Search Panel will slide out when the search link is clicked
 *
 * On Close: the menu should slide back into the left side and the primary overlay should disapear
 * On Close Search: the search panel will slide back, revealing the menu again
 * On Close Solo Search: If user came from Navigation Search then the search panel ONLY
 * will need to slide back - revealing the page again
 *
 * Calls for: Account Panel, Search Panel, UserNav
 *
 * @param {Boolean} props.isSoloSearchVisible :: determines if solo search panel should display
 * @param {Function} props.updateSoloSearchVisibility :: opens and closes solo search panel
 */
export function HamburgerMenu({ isSoloSearchVisible, updateSoloSearchVisibility }) {
	const {
		siteComponents: { HeaderLogo },
		siteConstants: { HAMBURGER_MENU_CORE_LINKS, HUB_LINKS },
	} = useContext(SiteConfigContext)

	const {
		search: { strokeColor },
	} = useTheme()

	const [isMenuVisible, setMenuVisibility] = useState(false)
	const [isSearchVisible, setSearchVisibility] = useState(false)
	const [isAccountPanelWrapperVisible, setAccountPanelWrapperVisibility] = useState(false)

	useEffect(() => {
		document.addEventListener('keydown', handleEscPanel)
	})

	// Set the window document body to hidden then the menus are visible, so the page is only clickable in the menu
	useEffect(() => {
		if (isMenuVisible || isSoloSearchVisible) {
			window.document.body.style.overflow = 'hidden'
			window.document.body.style.position = 'fixed'
		} else {
			window.document.body.style.overflow = 'visible'
			window.document.body.style.position = ''
		}
	}, [isMenuVisible, isSoloSearchVisible])

	// set the value of isSoloSearchVisible based on the props given,
	// this is when someone clicks on the search icon within the navigation bar
	useEffect(() => {
		if (isSoloSearchVisible) {
			return setSearchVisibility(isSoloSearchVisible)
		}
	}, [isSoloSearchVisible])

	// if any panels are open, set their state to false
	const handleClosePanel = () => {
		updateSoloSearchVisibility()
		return isSearchVisible
			? setSearchVisibility(!isSearchVisible)
			: isAccountPanelWrapperVisible
			? setAccountPanelWrapperVisibility(!isAccountPanelWrapperVisible)
			: setMenuVisibility(!isMenuVisible)
	}

	const handleEscPanel = (event) => {
		if (event.keyCode === 27) {
			return isSearchVisible
				? setSearchVisibility(false)
				: isAccountPanelWrapperVisible
				? setAccountPanelWrapperVisibility(false)
				: setMenuVisibility(false)
		}
	}

	// Toggles the Search panel on and off
	const handleSearchClick = () => {
		setSearchVisibility(!isSearchVisible)
	}

	return (
		<HamburgerMenuWrapper aria-expanded={isMenuVisible} menuStatus={isMenuVisible}>
			{/* HAMBURGER MENU AND BDC LOGO */}
			<OuterWrapper>
				<HamburgerWrapper onClick={handleClosePanel}>
					<Hamburger>
						<Layer menuStatus={isMenuVisible || isSearchVisible} />
						<Layer menuStatus={isMenuVisible || isSearchVisible} />
						<Layer menuStatus={isMenuVisible || isSearchVisible} />
					</Hamburger>
				</HamburgerWrapper>
				<HeaderLogo />
			</OuterWrapper>

			{/* Search Page Overlay - When this is open, the search appears over the menu panel */}
			<SearchPanel
				handleClosePanel={handleClosePanel}
				isSearchVisible={isSearchVisible}
				showBackToMenu={!isSoloSearchVisible}
			/>

			{/* Menu Overlay */}
			<MenuOverlay menuStatus={isMenuVisible}>
				<Menu>
					{/* TOOL BAR */}
					{/* Desktop :: default shows the search, login and sign up links */}
					<ToolBarDesktop>
						<StyledLink onClick={handleSearchClick}>
							Search
							<SearchIcon height={17} width={17} strokeColor={strokeColor} />
						</StyledLink>
					</ToolBarDesktop>
					{/* Mobile :: shows the search and login icon in the top right */}
					<ToolBarMobile>
						<ToolIcon onClick={handleSearchClick}>
							<SearchIconWrapper>
								<SearchIcon strokeColor={strokeColor} />
							</SearchIconWrapper>
						</ToolIcon>
					</ToolBarMobile>

					{/* MENU ELEMENTS */}
					{/* Core Value Props */}
					<CoreWrapper>
						{HAMBURGER_MENU_CORE_LINKS.map((item, i) => {
							return (
								<Element key={i}>
									<Link href={item.slug} target={item.target}>
										{item.title}
									</Link>
								</Element>
							)
						})}
					</CoreWrapper>
					{/* Business Basics */}
					<BusinessTopicsWrapper>
						<BusinessTopicsHeadline>Business Topics</BusinessTopicsHeadline>
						{HUB_LINKS.map((item, i) => {
							return (
								<Element key={i}>
									<Link href={`/${item.slug}/`}>{item.title}</Link>
								</Element>
							)
						})}
					</BusinessTopicsWrapper>
				</Menu>
			</MenuOverlay>

			{/* NAVY OVERLAY ON REST OF THE SITE */}
			<Overlay onClick={handleClosePanel} showOverlay={isMenuVisible || isSearchVisible} />
		</HamburgerMenuWrapper>
	)
}
HamburgerMenu.propTypes = {
	isSoloSearchVisible: bool,
	updateSoloSearchVisibility: func,
}
